import { takeLatest, call, put, all, select } from "redux-saga/effects";

//Custom Imports
import t from "@constants/localization";
import {
  getBusinessTips,
  getClTaskSummary,
  getFeedbackReasonClTask,
  getTaskData,
  getClTaskPrgressData,
} from "@services/api";
import { taskType } from "../reducers/actionTypes";
import clTaskAction from "../actions/clTask.action";
import {
  ABANDONED_CART,
  PROMOTION,
  WALLET_BALANCE_EXPIRY,
} from "@constants/clTaskTypes";
import analytics from "@services/analytics";
import { CL_TASKS_EVENTS } from "@constants/analyticsEventsCT";
import {
  abandonedBusinessTipsSelector,
  abandonedCartDataSelector,
  abandonedFeedbackSelector,
  promotionBusinessTipsSelector,
  promotionCartDataSelector,
  taskSummarySelector,
  walletBusinessTipsSelector,
  walletCartDataSelector,
  walletFeedbackSelector,
} from "@selectors/task.selector";

const PATH = "screens/ClTask";

function* updateClTaskData() {
  try {
    const { data } = yield call(getClTaskPrgressData);
    if (data?.todayDailyTasks) {
      yield put(
        clTaskAction.getTodayDailyTasks.success(data.todayDailyTasks, null),
      );
    }
  } catch (err) {
    yield put(clTaskAction.getTodayDailyTasks.error({ err }, null));
    console.log(err);
  }
}

function* getClTaskSummaryData() {
  try {
    yield call(updateClTaskData);
    const { data } = yield call(getClTaskSummary);
    const { api = {} } = yield select(taskSummarySelector);
    const { fetched = false } = api || {};

    if (data) {
      const { taskSummary, todaySummary } = data;
      let todayTask = [],
        weekDaysList = new Array(7).fill(0),
        activeDay = "";
      for (const key in todaySummary) {
        const item = todaySummary[key];
        let cardItem = {};
        switch (key) {
          case ABANDONED_CART:
            cardItem = {
              name: t(`${PATH}/customerInterested`),
              itemTitle: t(`${PATH}/cartValue`),
              itemValue: item?.totalCartValue
                ? `₹${parseInt(item.totalCartValue)}`
                : "₹0",
              doneTasks: item.doneTasks,
              totalTasks: item.totalTasks,
              iconName: "interestedCustomer",
              subtype: key,
            };
            todayTask.unshift(cardItem);
            break;
          case WALLET_BALANCE_EXPIRY:
            cardItem = {
              name: t(`${PATH}/cartWarning`),
              itemTitle: t(`${PATH}/customers`),
              itemValue: item?.totalUsers ? item.totalUsers : "0",
              doneTasks: item.doneTasks,
              totalTasks: item.totalTasks,
              iconName: "wallet",
              subtype: key,
            };
            todayTask.push(cardItem);
            break;
          case PROMOTION:
            cardItem = {
              name: t(`${PATH}/promotionBusiness`),
              itemTitle: t(`${PATH}/shareViaWhatsapp`),
              itemValue: "",
              doneTasks: item.doneTasks,
              totalTasks: item.totalTasks,
              iconName: PROMOTION,
              subtype: key,
            };
            todayTask.push(cardItem);
            break;
          case "day":
            activeDay = item;
            break;
          default:
            break;
        }
      }

      for (const key in taskSummary) {
        const item = taskSummary[key];
        const dayIndex = new Date(key).getDay();
        const dayName = t(`${PATH}/dayName/${dayIndex}`);
        let percentage = 0;
        if (item.totalTasks !== 0) {
          percentage =
            (100 * parseInt(item.doneTasks)) / parseInt(item.totalTasks);
          percentage = percentage.toFixed(0);
        }
        if (dayIndex === 0) {
          weekDaysList[6] = {
            dayName: dayName,
            isActive: activeDay === key,
            progress: percentage,
          };
        } else {
          weekDaysList[dayIndex - 1] = {
            dayName: dayName,
            isActive: activeDay === key,
            progress: percentage,
          };
        }
      }
      yield put(
        clTaskAction.getClTaskSummary.success(
          {
            todayTask,
            weekDaysList,
          },
          null,
        ),
      );
      if (!fetched) yield put(clTaskAction.getClTaskSummary.fetched({}, null));
    }
  } catch (err) {
    yield put(clTaskAction.getClTaskSummary.error({ err }, null));
    console.log(err);
  }
}

const trackCTEvent = ({ eventName, payload }) => {
  analytics.trackEvent(eventName, payload);
};

function* getPromotionTips() {
  try {
    const { data } = yield call(getBusinessTips, {
      subtype: PROMOTION,
    });
    const { api = {} } = yield select(promotionBusinessTipsSelector);
    const { fetched = false } = api || {};
    yield put(clTaskAction.getPromotionBusinessTips.success(data, null));
    if (!fetched)
      yield put(clTaskAction.getPromotionBusinessTips.fetched({}, null));
  } catch (err) {
    yield put(clTaskAction.getPromotionBusinessTips.error({ err }, null));
    console.log(err);
  }
}

function* getCartTips() {
  try {
    const { data } = yield call(getBusinessTips, {
      subtype: ABANDONED_CART,
    });
    const { api = {} } = yield select(abandonedBusinessTipsSelector);
    const { fetched = false } = api || {};
    yield put(clTaskAction.getAbandonedBusinessTips.success(data, null));
    if (!fetched)
      yield put(clTaskAction.getAbandonedBusinessTips.fetched({}, null));
  } catch (err) {
    yield put(clTaskAction.getAbandonedBusinessTips.error({ err }, null));
    console.log(err);
  }
}

function* getWalletTips() {
  try {
    const { data } = yield call(getBusinessTips, {
      subtype: WALLET_BALANCE_EXPIRY,
    });

    const { api = {} } = yield select(walletBusinessTipsSelector);
    const { fetched = false } = api || {};
    yield put(clTaskAction.getWalletBusinessTips.success(data, null));
    if (!fetched)
      yield put(clTaskAction.getWalletBusinessTips.fetched({}, null));
  } catch (err) {
    yield put(clTaskAction.getWalletBusinessTips.error({ err }, null));
    console.log(err);
  }
}

function* getCartData({ payload = {} }) {
  const { showConfetti = false } = payload;
  function groupBy(list, keyGetter) {
    const map = new Map();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }

  const processData = ({ data, app_link }) => {
    const grouped = Array.from(groupBy(data, (d) => d.id));
    const with_task_done_at = [],
      without_task_done_at = [],
      goal_achieved_view_data = [],
      show_on_top = [];
    grouped.forEach((item) => {
      const itemValue = item[1][0];
      if (
        itemValue?.goal_achieved_view === null &&
        itemValue?.goal_achieved != null &&
        itemValue?.goal_achieved_data != null
      ) {
        show_on_top.push(item);
      } else if (
        itemValue?.goal_achieved_data != null ||
        itemValue?.goal_achieved != null ||
        itemValue?.goal_achieved_view != null
      ) {
        goal_achieved_view_data.push(item);
      } else if (itemValue?.task_done_at != null) {
        with_task_done_at.push(item);
      } else {
        without_task_done_at.push(item);
      }
    });

    return {
      tasks: [
        ...show_on_top,
        ...without_task_done_at,
        ...with_task_done_at,
        ...goal_achieved_view_data,
      ],
      app_link: app_link,
    };
  };

  try {
    const { data } = yield call(getTaskData, {
      subtype: ABANDONED_CART,
    });
    const { api = {} } = yield select(abandonedCartDataSelector);
    const { fetched = false } = api || {};
    yield put(
      clTaskAction.getAbandonedCartData.success(
        processData({ data: data?.data, app_link: data?.app_link }),
        null,
      ),
    );
    if (!fetched)
      yield put(clTaskAction.getAbandonedCartData.fetched({}, null));

    if (showConfetti) {
      const { data: abandonedBusinessTips = {} } = yield select(
        abandonedBusinessTipsSelector,
      );
      const tipsData = abandonedBusinessTips?.data;
      if (
        tipsData?.totalTasks &&
        tipsData?.doneTasks &&
        tipsData?.totalTasks === tipsData?.doneTasks &&
        tipsData?.totalTasks != 0
      ) {
        yield put(clTaskAction.updateFireLoading(true, null));
        yield call(trackCTEvent, {
          eventName: CL_TASKS_EVENTS.TASK_COMPLETED,
          payload: {
            sub_task_id: ABANDONED_CART,
            task_type: ABANDONED_CART,
          },
        });
      }
    }
  } catch (err) {
    yield put(clTaskAction.getAbandonedCartData.error({ err }, null));
    console.log(err);
  }
}

function* getWalletData({ payload = {} }) {
  const { showConfetti = false } = payload;
  try {
    const { data } = yield call(getTaskData, {
      subtype: WALLET_BALANCE_EXPIRY,
    });
    const { api = {} } = yield select(walletCartDataSelector);
    const { fetched = false } = api || {};

    if (data?.data) {
      const with_task_done_at = [],
        without_task_done_at = [],
        goal_achieved_view_data = [],
        show_on_top = [];
      data?.data.forEach((item) => {
        if (
          item?.goal_achieved_view === null &&
          item?.goal_achieved != null &&
          item?.goal_achieved_data != null
        ) {
          show_on_top.push(item);
        } else if (
          item?.goal_achieved_data != null ||
          item?.goal_achieved != null ||
          item?.goal_achieved_view != null
        ) {
          goal_achieved_view_data.push(item);
        } else if (item?.task_done_at != null) {
          with_task_done_at.push(item);
        } else {
          without_task_done_at.push(item);
        }
      });
      data.data = [
        ...show_on_top,
        ...without_task_done_at,
        ...with_task_done_at,
        ...goal_achieved_view_data,
      ];
    }
    yield put(clTaskAction.getWalletData.success(data, null));
    if (!fetched) yield put(clTaskAction.getWalletData.fetched({}, null));

    if (showConfetti) {
      const { data: walletBusinessTips = {} } = yield select(
        walletBusinessTipsSelector,
      );
      const tipsData = walletBusinessTips?.data;
      if (
        tipsData?.totalTasks &&
        tipsData?.doneTasks &&
        tipsData?.totalTasks === tipsData?.doneTasks &&
        tipsData?.totalTasks != 0
      ) {
        yield put(clTaskAction.updateFireLoading(true, null));
        yield call(trackCTEvent, {
          eventName: CL_TASKS_EVENTS.TASK_COMPLETED,
          payload: {
            sub_task_id: WALLET_BALANCE_EXPIRY,
            task_type: WALLET_BALANCE_EXPIRY,
          },
        });
      }
    }
  } catch (err) {
    yield put(clTaskAction.getWalletData.error({ err }, null));
    console.log(err);
  }
}

function* getPromotionData({ payload = {} }) {
  const { showConfetti = false } = payload;
  try {
    const { data } = yield call(getTaskData, {
      subtype: PROMOTION,
    });
    const { api = {} } = yield select(promotionCartDataSelector);
    const { fetched = false } = api || {};
    yield put(clTaskAction.getPromotionData.success(data, null));
    if (!fetched) yield put(clTaskAction.getPromotionData.fetched({}, null));

    if (showConfetti) {
      const { data: promotionBusinessTips = {} } = yield select(
        promotionBusinessTipsSelector,
      );
      const tipsData = promotionBusinessTips?.data;
      if (
        tipsData?.totalTasks &&
        tipsData?.doneTasks &&
        tipsData?.totalTasks === tipsData?.doneTasks &&
        tipsData?.totalTasks != 0
      ) {
        yield put(clTaskAction.updateFireLoading(true, null));
        yield call(trackCTEvent, {
          eventName: CL_TASKS_EVENTS.TASK_COMPLETED,
          payload: {
            sub_task_id: PROMOTION,
            task_type: PROMOTION,
          },
        });
      }
    }
  } catch (err) {
    yield put(clTaskAction.getPromotionData.error({ err }, null));
    console.log(err);
  }
}

function* getFeedbackCart() {
  try {
    const { data } = yield call(getFeedbackReasonClTask, {
      subtype: ABANDONED_CART,
    });
    const { api = {} } = yield select(abandonedFeedbackSelector);
    const { fetched = false } = api || {};
    yield put(clTaskAction.getAbandonedFeedback.success(data, null));
    if (!fetched)
      yield put(clTaskAction.getAbandonedFeedback.fetched({}, null));
  } catch (err) {
    yield put(clTaskAction.getAbandonedFeedback.error({ err }, null));
    console.log(err);
  }
}

function* getFeedbackCashback() {
  try {
    const { data } = yield call(getFeedbackReasonClTask, {
      subtype: WALLET_BALANCE_EXPIRY,
    });
    const { api = {} } = yield select(walletFeedbackSelector);
    const { fetched = false } = api || {};
    yield put(clTaskAction.getWalletFeedback.success(data, null));
    if (!fetched) yield put(clTaskAction.getWalletFeedback.fetched({}, null));
  } catch (err) {
    yield put(clTaskAction.getWalletFeedback.error({ err }, null));
    console.log(err);
  }
}

export default function* () {
  yield all([
    takeLatest(taskType.GET_CL_TASK_SUMMARY_UPCOMING, getClTaskSummaryData),
    takeLatest(taskType.GET_TODAY_TASK_UPCOMING, updateClTaskData),
    takeLatest(taskType.ABANDONED_CART_UPCOMING, getCartData),
    takeLatest(taskType.ABANDONED_CART_BUSINESS_UPCOMING, getCartTips),
    takeLatest(taskType.GET_ABANDONED_FEEDBACK_UPCOMING, getFeedbackCart),
    takeLatest(taskType.GET_WALLET_DATA_TASK_UPCOMING, getWalletData),
    takeLatest(taskType.WALLET_BUSINESS_TIPS_UPCOMING, getWalletTips),
    takeLatest(taskType.GET_WALLET_FEEDBACK_UPCOMING, getFeedbackCashback),
    takeLatest(taskType.PROMOTION_BUSINESS_TIPS_UPCOMING, getPromotionTips),
    takeLatest(taskType.GET_PROMOTION_DATA_TASK_UPCOMING, getPromotionData),
  ]);
}
